import React, { useMemo } from 'react'
import { GridItem } from '@zuto/core/src/grid'
import { ArrowLeft, ArrowRight } from '@zuto/icons'
import CenteredButton from '../../style/CenteredCTA'
import { ZutoButtonProps } from '@zuto/atoms/src/Mui/Button/Button'

type NavigationButtonsProps = {
  // Optional text to render on the "continue" button.
  // Defaults to "Continue".
  nextText?: string,
  // Optional text to render on the "continue" button.
  // Defaults to "Previous".
  previousText?: string,
  // Function to be run when the "continue" button is clicked.
  nextHandler: () => void,
  // Function to be run when the "previous" button is clicked.
  previousHandler: () => void,
  // How to render the form "continue" button.
  // If `undefined` or `false`: The button will render as normal.
  // If `true` or `"disabled"`: The button will be greyed out, and unclickable.
  // If `"invisible"`: The button will not be rendered.
  disableNext?: boolean | 'invisible' | 'disabled',
  // How to render the form "previous" button.
  // If `undefined` or `false`: The button will render as normal.
  // If `true` or `"disabled"`: The button will be greyed out, and unclickable.
  // If `"invisible"`: The button will not be rendered.
  disablePrevious?: boolean | 'invisible' | 'disabled',
  // Optional props to be passed directly to the "continue" button.
  // Useful for applying custom styling.
  nextButtonProps?: ZutoButtonProps,
  // Optional props to be passed directly to the "previous" button.
  // Useful for applying custom styling.
  previousButtonProps?: ZutoButtonProps,
  hideNext?: boolean
}

const NavigationButtons = (
  {
    nextText = 'Continue',
    previousText = 'Previous',
    nextHandler,
    previousHandler,
    disableNext,
    disablePrevious,
    nextButtonProps,
    previousButtonProps,
    hideNext = false
  }: NavigationButtonsProps
): JSX.Element => {
  const disableNextButton: boolean = useMemo(
    () => disableNext === 'disabled' || disableNext === true,
    [disableNext]
  )
  const disablePreviousButton: boolean = useMemo(
    () => disablePrevious === 'disabled' || disablePrevious === true,
    [disablePrevious]
  )
  const hideNextButton: boolean = useMemo(
    () => disableNext === 'invisible',
    [disableNext]
  )
  const hidePreviousButton: boolean = useMemo(
    () => disablePrevious === 'invisible',
    [disablePrevious]
  )

  const continueButton = (
    <CenteredButton
      {...nextButtonProps}
      endSvgIcon={ArrowRight}
      onClick={nextHandler}
      disabled={disableNextButton}
      data-testid="continue-button-centered"
    >
      {nextText}
    </CenteredButton>
  )

  const previousButton = (
    <CenteredButton
      {...previousButtonProps}
      variant="text"
      startSvgIcon={ArrowLeft}
      onClick={previousHandler}
      disabled={disablePreviousButton}
      data-testid="previous-button-centered"
      subText="(You won't lose your information)"
      size="large"
    >
      {previousText}
    </CenteredButton>
  )

  return (
    <>
      <GridItem
        display="flex"
        alignItems="center"
        gridColumn={['1 / span 12', '7 / span 4']}
        gridRow={['auto', '5 / auto']}
        mb={4}
      >
        {!hideNextButton && !hideNext && continueButton}
      </GridItem>
      <GridItem
        gridColumn={['1 / span 12', '3 / span 4']}
        gridRow={['auto', '5 / auto']}
      >
        {!hidePreviousButton && previousButton}
      </GridItem>
    </>
  )
}

export default NavigationButtons
